import {
    integerColDefCreator,
    statusColDefCreator,
    stringColDefCreator,
    modifyButtonColDefCreator,
    signaturePeriodColDefCreator,
    referenceableColDefCreator,
    numberColDefCreator,
} from "./standard-col-def-creators"

import { companyGroupsFilterDef, projectStatusFilterDef, multiProjectFilterDef } from "../../../filters/filter-defs"
import { projectDashboardUploadRoutePath } from "../../../router/constants"
import { getFlagEnabled } from "../../../getFlagValue"
import { cohortNameValueGetter, employeeCompanySuppliedIdsValueGetter } from "../../../common/ag-grid-value-getters"

export function getProjectsListViewSettings(featureFlags, _currentProject, currentUser) {
    const hide_groups = !featureFlags.groups
    const hide_equipment = !featureFlags.equipment
    const hide_materials = !featureFlags.materials

    const textFieldOptions =
        currentUser && currentUser.text_field_options
            ? currentUser.text_field_options.filter(option => option.model === "Project")
            : []

    const textFieldOptionColDefs = textFieldOptions.map(option =>
        stringColDefCreator({
            headerName: option.label,
            field: `/custom_fields/${option.name}`,
            cellClass: ["readonly"],
            editable: false,
        })
    )

    const cicoMealsAndBreaksColumns = getFlagEnabled("WA-8881-cico-meal-break-min")
        ? [
              stringColDefCreator({
                  headerName: "CI/CO Meals Enabled",
                  field: "/cico_settings/meals_enabled",
                  hide: true,
                  valueFormatter: params => (params.value ? "Yes" : "No"),
              }),
              stringColDefCreator({
                  headerName: "CI/CO Breaks Enabled",
                  field: "/cico_settings/breaks_enabled",
                  hide: true,
                  valueFormatter: params => (params.value ? "Yes" : "No"),
              }),
              integerColDefCreator({
                  headerName: "CI/CO Minimum Break Length",
                  field: "/cico_settings/min_break_length",
                  hide: true,
              }),
              integerColDefCreator({
                  headerName: "CI/CO Minimum Meal Length",
                  field: "/cico_settings/min_meal_length",
                  hide: true,
              }),
          ]
        : [
              {
                  ...stringColDefCreator({
                      headerName: "CI/CO Meals and Breaks",
                      field: "/cico_settings/meals_and_breaks",
                      hide: true,
                      valueFormatter: params => (params.value ? "Yes" : "No"),
                  }),
              },
          ]

    const extraCicoColumns = getFlagEnabled("WA-8877-project-csv-cico-settings")
        ? [
              ...cicoMealsAndBreaksColumns,
              {
                  ...stringColDefCreator({
                      headerName: "CI/CO Photo",
                      field: "/cico_settings/photo",
                      hide: true,
                  }),
              },
              ...(getFlagEnabled("WA-8883-employee-cohort-cico-overrides")
                  ? [
                        {
                            ...referenceableColDefCreator({
                                headerName: "CI/CO Photo Employee Overrides",
                                field: "/cico_settings/photo_employee_overrides",
                                hide: true,
                                multiSelect: true,
                                resourceName: "employees",
                            }),
                            valueGetter: employeeCompanySuppliedIdsValueGetter,
                        },
                        {
                            ...referenceableColDefCreator({
                                headerName: "CI/CO Photo Cohort Overrides",
                                field: "/cico_settings/photo_cohort_overrides",
                                hide: true,
                                multiSelect: true,
                                resourceName: "cohorts",
                            }),
                            valueGetter: cohortNameValueGetter,
                        },
                    ]
                  : []),
              {
                  ...stringColDefCreator({
                      headerName: "CI/CO Location",
                      field: "/cico_settings/location",
                      hide: true,
                  }),
              },
              ...(getFlagEnabled("WA-8883-employee-cohort-cico-overrides")
                  ? [
                        {
                            ...referenceableColDefCreator({
                                headerName: "CI/CO Location Employee Overrides",
                                field: "/cico_settings/location_employee_overrides",
                                hide: true,
                                multiSelect: true,
                                resourceName: "employees",
                            }),
                            valueGetter: employeeCompanySuppliedIdsValueGetter,
                        },
                        {
                            ...referenceableColDefCreator({
                                headerName: "CI/CO Location Cohort Overrides",
                                field: "/cico_settings/location_cohort_overrides",
                                hide: true,
                                multiSelect: true,
                                resourceName: "cohorts",
                            }),
                            valueGetter: cohortNameValueGetter,
                        },
                    ]
                  : []),
              {
                  ...stringColDefCreator({
                      headerName: "CI/CO Custom Address",
                      field: "/cico_settings/custom_address",
                      hide: true,
                  }),
              },
              {
                  ...stringColDefCreator({
                      headerName: "CI/CO Rounding Increment",
                      field: "/cico_settings/rounding_increment",
                      hide: true,
                  }),
              },
              {
                  ...stringColDefCreator({
                      headerName: "CI/CO Units",
                      field: "/cico_settings/units",
                      hide: true,
                  }),
              },
              {
                  ...numberColDefCreator({
                      headerName: "CI/CO Radius",
                      field: "/cico_settings/radius",
                      hide: true,
                  }),
              },
          ]
        : []

    return {
        tableName: "Projects",
        navId: "projects",
        resources: ["projects"],
        filters: [companyGroupsFilterDef, projectStatusFilterDef, multiProjectFilterDef],
        additionalQueryParams: { expanded_annotations: true, include_cico_overrides: true },
        relatedNames: {
            projects: {
                employees: [
                    "/cico_settings/location_employee_overrides",
                    "/cico_settings/photo_employee_overrides",
                ],
                cohorts: ["/cico_settings/location_cohort_overrides", "/cico_settings/photo_cohort_overrides"],
            },
        },
        colDefs: [
            stringColDefCreator({
                headerName: "Job #",
                field: "/job_number",
                sort: "desc",
            }),
            stringColDefCreator({
                headerName: "Project Name",
                field: "/name",
            }),
            stringColDefCreator({
                headerName: "Address",
                field: "/address",
                width: 320,
            }),
            stringColDefCreator({
                headerName: "Group",
                field: "/group_name",
                hide: hide_groups,
            }),
            statusColDefCreator(),
            ...(featureFlags.cico
                ? [
                      {
                          ...stringColDefCreator({
                              headerName: "CI/CO Enabled",
                              field: "/cico_settings/enabled",
                              editable: false,
                              valueFormatter: params => (params.value ? "Yes" : "No"),
                          }),
                          maxWidth: 150,
                      },
                      ...extraCicoColumns,
                  ]
                : []),
            stringColDefCreator({
                headerName: "Client",
                field: "/client_name",
            }),
            ...textFieldOptionColDefs,
            signaturePeriodColDefCreator(),
            {
                ...integerColDefCreator({
                    headerName: "Team Members",
                    field: "/employees_count",
                }),
                excludeFromExport: true,
            },
            {
                ...integerColDefCreator({
                    headerName: "Cost Codes",
                    field: "/cost_codes_count",
                }),
                excludeFromExport: true,
            },
            {
                ...integerColDefCreator({
                    headerName: "Equipment",
                    field: "/equipment_count",
                    hide: hide_equipment,
                }),
                excludeFromExport: true,
            },
            {
                ...integerColDefCreator({
                    headerName: "Materials",
                    field: "/materials_count",
                    hide: hide_materials,
                }),
                excludeFromExport: true,
            },
            {
                ...modifyButtonColDefCreator({
                    headerName: "Settings",
                    field: "/settings",
                }),
                excludeFromExport: true,
            },
        ],
        gridSettings: {
            rowHeight: 40,
            defaultColDef: {
                editable: false,
            },
            // SSRM Settings
            rowModelType: "serverSide",
            serverSideStoreType: "partial",
            rowSelection: "multiple",
            cacheBlockSize: 100,
            suppressMultiSort: true,
        },
        otherSettings: {
            buttons: {
                table: [
                    {
                        label: "Create Project",
                        icon: "add",
                        action: "addProject",
                    },
                    {
                        label: "Upload",
                        icon: "upload",
                        action: "navigateTo",
                        args: {
                            url: projectDashboardUploadRoutePath,
                        },
                    },
                    ...(getFlagEnabled("WA-8877-project-csv-cico-settings")
                        ? [
                              {
                                  label: "Export ▾",
                                  icon: "export",
                                  action: "export",
                              },
                          ]
                        : []),
                ],
            },
            enableSearchBar: true,
            updateOnSave: true,
        },
    }
}
